/* iter55g60 — Editor capability-guarding hook.
 *
 * Groups the tier-gating decisions that used to sprawl inside
 * EditorPanel.jsx: which actions are permitted for the current
 * subscription plan, the guarded wrapper around generateChapter that
 * diverts Basic users to an upgrade toast, and the draft-entire-novel
 * readiness-dialog gate.
 *
 * Keeping this in one place means the panel just consumes flags +
 * handlers and never has to reason about capability keys directly.
 *
 * Add-on credits: a user who purchased a per-use credit (e.g.
 * bulk_draft_1, continuity_audit_1) gets the same UI unlock as a tier
 * subscriber — hasCredit() from useBonusAddons bridges the gap.
 */
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { useCapabilities } from '@/lib/useCapabilities';
import { useBonusAddons } from '@/lib/useBonusAddons';

export function useEditorCapabilities({ generateChapter }) {
  const { can: capsCan, upgradeCta } = useCapabilities();
  const { hasCredit } = useBonusAddons();

  // Tier capability OR purchased add-on credit unlocks the feature in the UI.
  const canAiWriteChapters = capsCan('ai_write_chapters');
  const canBulkDraft       = capsCan('bulk_draft_novel')  || hasCredit('bulk_draft');
  const canContinuityAudit = capsCan('full_novel_review') || hasCredit('continuity_audit');
  const canScriptConvert   = capsCan('convert_to_script') || hasCredit('script_conversion');
  const canExpandWordCount = capsCan('word_count_expand');

  const [draftNovelOpen, setDraftNovelOpen] = useState(false);

  const guardedGenerateChapter = useCallback((cid) => {
    if (!canAiWriteChapters) {
      const cta = upgradeCta('ai_write_chapters');
      toast.info(cta.title, {
        description: `${cta.body} Claude Coach on Basic still suggests what could come next — try "Continue" instead. · Upgrade in Settings → Subscription.`,
        duration: 6000,
      });
      return;
    }
    generateChapter(cid);
  }, [canAiWriteChapters, upgradeCta, generateChapter]);

  const openDraftNovel  = useCallback(() => setDraftNovelOpen(true),  []);
  const closeDraftNovel = useCallback(() => setDraftNovelOpen(false), []);

  return {
    upgradeCta,
    canAiWriteChapters,
    canBulkDraft,
    canContinuityAudit,
    canScriptConvert,
    canExpandWordCount,
    guardedGenerateChapter,
    draftNovelOpen,
    openDraftNovel,
    closeDraftNovel,
  };
}
